import { useEffect, useState } from 'react'
import { IDigitalAddress } from '../../../../types/contact';
import { Table, Input } from 'reactstrap';

import { EditIcon, CheckMarkIcon, CancelIcon } from '../../../common/icons';
import { CheckGroup } from '../../../../hook/useCheckGroup';
import useToggle from '../../../../hook/useToggle';
import { useLazyQuery, useMutation } from '@apollo/client';
import SpinnerOverlay from '../../..//common/spinner/spinner';
import { notifySuccess } from '../../../../services/notification';
import { GET_POSITION_EMAILS, SET_POSITION_PRIMARY_EMAIL } from '../../../../graphql/contact-gql';
import EmailUpdate from './email-update';
import { IPosition } from '../../../../types/position';
import ImgButton from '../../../common/img-button/img-button';
import useAuth from '../../../../hook/useAuth';

type PositionEmailProps = {
    position: IPosition;
    checkGroup: CheckGroup<number>
}

const PositionEmail = (props: PositionEmailProps) => {
    const {position, checkGroup} = props;
    const { isFrenchTranslator } = useAuth()
    const [digitalAddress, setDigitalAddress] = useState<IDigitalAddress>()
    const {isOpen: editDigitalAddressOpen, toggle: toogleEditDigitalAddress} = useToggle()
    const [cloned,setCloned] = useState<boolean>(false);
    const [getPositionEmails, {loading, data}] = useLazyQuery(GET_POSITION_EMAILS, {
        onCompleted: (data) => {
            if (data?.position?.digitalAddresses) {
                checkGroup.init(data.position.digitalAddresses.map((e: IDigitalAddress) => e.id))
            }
        }
    })

    const [setPrimaryAddress, {loading: loadingPrimary}] = useMutation(SET_POSITION_PRIMARY_EMAIL)

    useEffect(() => {
        setCloned(!!position?.shareLinkId)
        if (position) {
            getPositionEmails({variables: {positionId: position.positionId}})
        }
    }, [position])

    const editDigitalAddress = (digitalAddress: IDigitalAddress) => {
        setDigitalAddress(digitalAddress)
        toogleEditDigitalAddress()
    }

    const setPrimary = (digitalAddress: IDigitalAddress) => {
        setPrimaryAddress({
            variables: {
                positionId: digitalAddress.businessEntityId,
                digitalAddressId: digitalAddress.id
            },
            refetchQueries: [{
                query: GET_POSITION_EMAILS,
                variables: { positionId: digitalAddress?.businessEntityId }
            }],
            awaitRefetchQueries: true,
            onCompleted: (data) => data && notifySuccess()
        })
    }

    const visibleColumnsCount = () : number => {
        // french translator won't see the select or edit columns
        return isFrenchTranslator ? 2 : 4;
    }

    return (<>
        {(loading || loadingPrimary) && <SpinnerOverlay />}
        {data?.position?.digitalAddresses &&
            <Table striped id="position_emails" className="fixed">
                <thead>
                    <tr>
                        {!isFrenchTranslator && <th className="icon-column">
                            <span className="sr-only">select email</span>
                            {data?.position?.digitalAddresses?.length > 0 && !checkGroup.isEmpty() &&
                                <>
                                    <span id="posEmailSelectAllLbl" className="sr-only">Select all</span>
                                    <Input
                                        type="checkbox"
                                        title="Select all"
                                        aria-labelledby="posEmailSelectAllLbl"
                                        checked={checkGroup.isAllChecked()}
                                        onChange={checkGroup.onCheckAll}
                                        disabled={cloned}
                                    />
                                </>
                            }
                        </th>
                        }
                        <th>Email Address</th>
                        <th className="primary-mailing-column">Primary</th>
                        {!isFrenchTranslator && <th className="edit-column">Edit</th>}
                    </tr>
                </thead>
                <tbody>
                    { data?.position?.digitalAddresses.length === 0
                        ? <tr><td colSpan={visibleColumnsCount()} style={{textAlign: 'center'}}>No {position.isRole ? 'role' : 'position'} email addresses found.</td></tr>
                        : data?.position?.digitalAddresses?.map((digitalAddress: IDigitalAddress, index: number) => {
                            return (
                                <tr key={digitalAddress.id}>
                                    {!isFrenchTranslator && <td>
                                        <Input type="checkbox"
                                            aria-label={'Select digital address ' + digitalAddress.emailAddressTxt }
                                            checked={checkGroup.isChecked(digitalAddress.id)}
                                            onChange={event => checkGroup.onCheck(event, digitalAddress.id)}
                                            disabled={cloned}
                                        />
                                    </td>
                                    }
                                    <td>{digitalAddress.emailAddressTxt }</td>
                                    <td>
                                        {isFrenchTranslator && <> {digitalAddress.assignType.code === "PRIMARY" ? <CheckMarkIcon title="Primary" /> : <CancelIcon />}</>}
                                        {!isFrenchTranslator && <>
                                            { digitalAddress.assignType.code === "PRIMARY"
                                                ? <CheckMarkIcon title="Primary"></CheckMarkIcon>
                                                : <Input type="checkbox"
                                                    aria-label="Set primary email"
                                                    checked={digitalAddress.assignType.code === "PRIMARY"}
                                                    onChange={()=>setPrimary(digitalAddress)}
                                                    disabled={cloned}/>
                                            }
                                            </>
                                        }
                                    </td>
                                    {!isFrenchTranslator && <td>
                                        <ImgButton aria-label="Edit email" title="Edit email" onClick={()=>editDigitalAddress(digitalAddress)} disabled={cloned}>
                                            <EditIcon className="edit-pencil"/>
                                        </ImgButton>
                                    </td>
                                    }
                                </tr>
                                )
                            })
                        }
                    </tbody>
                </Table>
            }

            <EmailUpdate digitalAddress={digitalAddress} isRole={position?.isRole} isOpen={editDigitalAddressOpen} toggle={toogleEditDigitalAddress}/>
        </>
    )
}

export default PositionEmail
